import { FormTab, FormTabProps, Link, useInput, useGetList, useRecordContext, useNotify, Loading } from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect } from 'react';

import CraftPageSection from 'src/components/CraftPageSection';
import { CriteriaGroupRecord, OrganizationRecord } from 'src/types';
import { updateSeverityConfig, updateIncludeDpConfig, updateIncludeGupConfig } from '../helpers';
import { SEVERITY, SEVERITY_NAME } from '../OrganizationEnums';

const RiskScansTab = (props: Omit<FormTabProps, 'label'>) => {
  const theme = useTheme();
  const record = useRecordContext<OrganizationRecord>();
  const notify = useNotify();
  const {
    field: { value, onChange },
  } = useInput({ source: 'criteria_groups_transformed' });
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const [severitiesByCriterionIdMap, setSeveritiesByCriterionIdMap] = useState<Record<string, number>>({});
  const [includeDpByCriterionIdMap, setIncludeDpByCriterionIdMap] = useState<Record<string, boolean>>({});
  const [includeGupByCriterionIdMap, setIncludeGupByCriterionIdMap] = useState<Record<string, boolean>>({});

  // Get all available criteria groups
  const { data: allCriteriaGroups, isLoading } = useGetList<CriteriaGroupRecord>('criteria_groups', {
    filter: { 'archived_at@_is_null': true },
    sort: { field: 'name', order: 'ASC' },
    meta: { noLimit: true },
  });

  useEffect(() => {
    if (record?.organizations_criteria_configs) {
      const configs: Record<string, number> = {};
      const includeDpConfigs: Record<string, boolean> = {};
      const includeGupConfigs: Record<string, boolean> = {};
      record.organizations_criteria_configs.forEach((config) => {
        if (!config.archived_at) {
          configs[config.criterion_id] = config.severity;
          includeDpConfigs[config.criterion_id] = config.include_dp;
          includeGupConfigs[config.criterion_id] = config.include_gup;
        }
      });
      setSeveritiesByCriterionIdMap(configs);
      setIncludeDpByCriterionIdMap(includeDpConfigs);
      setIncludeGupByCriterionIdMap(includeGupConfigs);
    }
  }, [record?.organizations_criteria_configs]);

  const handleAccordionChange = (accordionId: string) => {
    const newExpanded = new Set(expandedAccordions);
    if (newExpanded.has(accordionId)) {
      newExpanded.delete(accordionId);
    } else {
      newExpanded.add(accordionId);
    }
    setExpandedAccordions(newExpanded);
  };

  const handleSeverityChange = (criterionId: string, newSeverity: number) => {
    if (!record) return;

    const isInAnySelectedGroup = allCriteriaGroups?.some((group) => {
      if (!(value || []).includes(group.id)) return false;
      return group.criteria?.some((criterionMapping) => criterionMapping.criterion.id === criterionId);
    });

    if (!isInAnySelectedGroup) {
      notify('Cannot edit severity for unselected risk scans', { type: 'warning' });
      return;
    }

    setSeveritiesByCriterionIdMap((prev) => ({
      ...prev,
      [criterionId]: newSeverity,
    }));

    updateSeverityConfig(record.id, criterionId, newSeverity)
      .then(() => {
        notify('Severity configuration saved successfully', { type: 'success' });
      })
      .catch(() => {
        notify('Failed to save severity configuration', { type: 'error' });
      });
  };

  const handleIncludeDpChange = (criterionId: string, includeDp: boolean) => {
    if (!record) return;

    const isInAnySelectedGroup = allCriteriaGroups?.some((group) => {
      if (!(value || []).includes(group.id)) return false;
      return group.criteria?.some((criterionMapping) => criterionMapping.criterion.id === criterionId);
    });

    if (!isInAnySelectedGroup) {
      notify('Cannot edit configuration for unselected risk scans', { type: 'warning' });
      return;
    }

    setIncludeDpByCriterionIdMap((prev) => ({
      ...prev,
      [criterionId]: includeDp,
    }));

    updateIncludeDpConfig(record.id, criterionId, includeDp)
      .then(() => {
        notify('Include Direct Parent configuration saved successfully', { type: 'success' });
      })
      .catch(() => {
        notify('Failed to save Include Direct Parent configuration', { type: 'error' });
      });
  };

  const handleIncludeGupChange = (criterionId: string, includeGup: boolean) => {
    if (!record) return;

    const isInAnySelectedGroup = allCriteriaGroups?.some((group) => {
      if (!(value || []).includes(group.id)) return false;
      return group.criteria?.some((criterionMapping) => criterionMapping.criterion.id === criterionId);
    });

    if (!isInAnySelectedGroup) {
      notify('Cannot edit configuration for unselected risk scans', { type: 'warning' });
      return;
    }

    setIncludeGupByCriterionIdMap((prev) => ({
      ...prev,
      [criterionId]: includeGup,
    }));

    updateIncludeGupConfig(record.id, criterionId, includeGup)
      .then(() => {
        notify('Include Global Ultimate Parent configuration saved successfully', { type: 'success' });
      })
      .catch(() => {
        notify('Failed to save Include Global Ultimate Parent configuration', { type: 'error' });
      });
  };

  const handleCheckboxChange = (criteriaGroupId: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    let newValues;

    if (checked) {
      if (!currentValues.includes(criteriaGroupId)) {
        newValues = [...currentValues, criteriaGroupId];
      } else {
        newValues = currentValues;
      }
    } else {
      newValues = currentValues.filter((id: string) => id !== criteriaGroupId);
    }

    onChange(newValues);
  };

  if (isLoading) {
    return (
      <FormTab label="Risk Scans" {...props}>
        <CraftPageSection title="Risk Scans">
          <Loading />
        </CraftPageSection>
      </FormTab>
    );
  }

  return (
    <FormTab label="Risk Scans" {...props}>
      <CraftPageSection title="Risk Scans">
        <Box>
          {allCriteriaGroups?.map((criteriaGroup) => {
            const activeCriteria =
              criteriaGroup.criteria?.filter((criterionMapping) => !criterionMapping.criterion.archived_at) || [];

            const isSelected = (value || []).includes(criteriaGroup.id);
            const isExpanded = expandedAccordions.has(criteriaGroup.id);

            return (
              <Accordion
                key={criteriaGroup.id}
                expanded={isExpanded}
                onChange={() => handleAccordionChange(criteriaGroup.id)}
                sx={{
                  mb: 1,
                  '&.Mui-expanded': {
                    margin: '8px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleCheckboxChange(criteriaGroup.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {criteriaGroup.name}
                      </Typography>
                    }
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ ml: 2 }}>
                    {!isSelected ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                        Enable this risk scan to view and configure risk signals
                      </Typography>
                    ) : (
                      activeCriteria.map((criterionMapping) => {
                        const { criterion } = criterionMapping;
                        const severity = severitiesByCriterionIdMap[criterion.id];
                        const includeDp = includeDpByCriterionIdMap[criterion.id] ?? false;
                        const includeGup = includeGupByCriterionIdMap[criterion.id] ?? false;

                        return (
                          <Box
                            key={criterion.id}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                              border: `1px solid ${theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'}`,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Link to={`/criteria/${criterion.id}/show`}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {criterion.name}
                                </Typography>
                              </Link>
                              <Chip label={criterion.source} color="secondary" size="small" />
                            </Box>
                            {criterion.description && (
                              <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="body2" color="text.secondary">
                                  {criterion.description}
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {SEVERITY_NAME[SEVERITY.NORMAL]}
                                  </Typography>
                                  <Switch
                                    checked={severity === SEVERITY.CRITICAL}
                                    onChange={(e) =>
                                      handleSeverityChange(
                                        criterion.id,
                                        e.target.checked ? SEVERITY.CRITICAL : SEVERITY.NORMAL,
                                      )
                                    }
                                    size="small"
                                    disabled={!isSelected}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: 'error.main',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: 'error.main',
                                      },
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    {SEVERITY_NAME[SEVERITY.CRITICAL]}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={includeDp}
                                      onChange={(e) => handleIncludeDpChange(criterion.id, e.target.checked)}
                                      disabled={!isSelected}
                                    />
                                  }
                                  label="Include Direct Parent"
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={includeGup}
                                      onChange={(e) => handleIncludeGupChange(criterion.id, e.target.checked)}
                                      disabled={!isSelected}
                                    />
                                  }
                                  label="Include Global Ultimate Parent"
                                />
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </CraftPageSection>
    </FormTab>
  );
};

export default RiskScansTab;
